import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { IconEye } from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convex-api";

type ErrandStateFilter =
	| "all"
	| "posted"
	| "negotiation"
	| "awaiting_agreement_confirmation"
	| "funds_staged"
	| "in_progress"
	| "awaiting_delivery_confirmation"
	| "completed"
	| "disputed"
	| "cancelled";

const STATE_LABELS: Record<string, string> = {
	posted: "Posted",
	negotiation: "Negotiation",
	awaiting_agreement_confirmation: "Awaiting Agreement",
	funds_staged: "Funds Staged",
	in_progress: "In Progress",
	awaiting_delivery_confirmation: "Awaiting Delivery",
	completed: "Completed",
	disputed: "Disputed",
	cancelled: "Cancelled",
};

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString(`en-NG`, { minimumFractionDigits: 2 })}`;
}

function getStateBadge(state: string) {
	switch (state) {
		case "completed":
			return <Badge className="bg-green-500">{STATE_LABELS[state]}</Badge>;
		case "cancelled":
			return <Badge variant="secondary">{STATE_LABELS[state]}</Badge>;
		case "disputed":
			return <Badge variant="destructive">{STATE_LABELS[state]}</Badge>;
		case "in_progress":
		case "awaiting_delivery_confirmation":
			return <Badge className="bg-blue-500">{STATE_LABELS[state]}</Badge>;
		default:
			return <Badge variant="outline">{STATE_LABELS[state] ?? state}</Badge>;
	}
}

export const Route = createFileRoute("/_authenticated/errands/")({
	component: ErrandsPage,
});

function ErrandsPage() {
	const [stateFilter, setStateFilter] = useState<ErrandStateFilter>("all");

	const data = useQuery(api.admin.listErrands, {
		state: stateFilter === "all" ? undefined : stateFilter,
		limit: 50,
	});
	const isLoading = data === undefined;

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8 flex items-center justify-between">
				<div>
					<h1 className="font-bold text-3xl">Errand Management</h1>
					<p className="mt-1 text-muted-foreground text-sm">
						Monitor and manage all platform errands.
					</p>
				</div>
				<Select
					onValueChange={(value) => setStateFilter(value as ErrandStateFilter)}
					value={stateFilter}
				>
					<SelectTrigger className="w-[220px]">
						<SelectValue placeholder="Filter by state" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All States</SelectItem>
						<SelectItem value="posted">Posted</SelectItem>
						<SelectItem value="negotiation">Negotiation</SelectItem>
						<SelectItem value="funds_staged">Funds Staged</SelectItem>
						<SelectItem value="in_progress">In Progress</SelectItem>
						<SelectItem value="awaiting_delivery_confirmation">
							Awaiting Delivery
						</SelectItem>
						<SelectItem value="completed">Completed</SelectItem>
						<SelectItem value="disputed">Disputed</SelectItem>
						<SelectItem value="cancelled">Cancelled</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{isLoading ? (
				<div className="space-y-3">
					{Array.from({ length: 8 }).map((_, i) => (
						<Skeleton className="h-12 w-full" key={`skeleton-${String(i)}`} />
					))}
				</div>
			) : !data || data.length === 0 ? (
				<div className="flex items-center justify-center rounded-md border py-10">
					<p className="text-muted-foreground">No errands found.</p>
				</div>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Title</TableHead>
								<TableHead>Customer</TableHead>
								<TableHead>Runner</TableHead>
								<TableHead>Category</TableHead>
								<TableHead>State</TableHead>
								<TableHead>Budget</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.map((errand: (typeof data)[number]) => (
								<TableRow key={errand._id}>
									<TableCell className="max-w-[200px] truncate font-medium">
										{errand.title}
									</TableCell>
									<TableCell>
										{errand.customer ? (
											<Link
												to="/customers/$userId"
												params={{ userId: errand.customer.id }}
												className="text-blue-600 underline-offset-2 hover:underline"
											>
												{errand.customer.name}
											</Link>
										) : (
											"N/A"
										)}
									</TableCell>
									<TableCell>
										{errand.runner ? (
											<Link
												to="/runners/$userId"
												params={{ userId: errand.runner.id }}
												className="text-blue-600 underline-offset-2 hover:underline"
											>
												{errand.runner.name}
											</Link>
										) : (
											<span className="text-muted-foreground">Unassigned</span>
										)}
									</TableCell>
									<TableCell>{errand.category?.displayName ?? "N/A"}</TableCell>
									<TableCell>{getStateBadge(errand.state)}</TableCell>
									<TableCell>
										{errand.shoppingBudget
											? formatNaira(errand.shoppingBudget)
											: "N/A"}
									</TableCell>
									<TableCell>
										{new Date(errand.createdAt).toLocaleDateString()}
									</TableCell>
									<TableCell>
										<Button asChild size="sm" variant="outline">
											<Link
												to="/errands/$errandId"
												params={{ errandId: errand._id }}
											>
												<IconEye className="mr-2 h-4 w-4" />
												View
											</Link>
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
